import { useState, type ReactNode } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { FileCode, ExternalLink, ChevronRight, ChevronDown } from 'lucide-react'
import { scansApi } from '../api/scans'
import { AppShell } from '../components/layout/AppShell'
import { SummaryPanel } from '../components/findings/SummaryPanel'
import { CheckedTab } from '../components/findings/CheckedTab'
import { agentCategories, agentName } from '../components/findings/checkedModel'
import { ScanModeBanner } from '../components/ScanModeBanner'
import { ScanSourceManifestNote } from '../components/SourceManifestNote'
import { dateTimeLabel } from '../utils/format'
import { ranHeader } from '../utils/runTime'
import { FindingCard } from '../components/findings/FindingCard'
import { Spinner } from '../components/shared/Spinner'
import { PillDot, SeverityBadge, TonePill } from '../components/shared/Badge'
import { SEVERITY_LABEL } from '../components/shared/pillTones'
import type { FindingResponse, FeedbackVerdict, Severity } from '../types/api'
import { SUBCATEGORY_LABELS } from '../types/api'

// ---------------------------------------------------------------------------
// Types & constants
// ---------------------------------------------------------------------------

type GroupMode = 'severity' | 'file'

/** DEC-189: the tab lives in the URL (`?tab=checked`) so it can be linked. */
type PageTab = 'findings' | 'checked'

const SEVERITIES: Severity[] = ['critical', 'high', 'medium', 'low']

const SEVERITY_ORDER: Record<string, number> = {
  critical: 0, high: 1, medium: 2, low: 3,
}

// Severity is the one pill style (DEC-193): an outline, a dot and ink text.
function SevPill({ severity, children }: { severity: string; children?: ReactNode }) {
  const known = SEVERITIES.find((k) => k === severity)
  return known ? (
    <SeverityBadge severity={known}>{children}</SeverityBadge>
  ) : (
    <TonePill tone="neutral">{children ?? severity}</TonePill>
  )
}


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function severityOrder(s: Severity): number {
  return SEVERITY_ORDER[s] ?? 99
}

function sortFindings(findings: FindingResponse[]): FindingResponse[] {
  return [...findings].sort((a, b) => {
    const sd = severityOrder(a.severity) - severityOrder(b.severity)
    if (sd !== 0) return sd
    return b.estimated_effort_hours - a.estimated_effort_hours
  })
}

/** Group findings by file. Returns ordered list of [file, findings[]] pairs.
 *  Grouped findings (occurrence_count > 1) go into a special "Multiple files" bucket.
 */
function groupByFile(
  findings: FindingResponse[],
): Array<{ file: string; findings: FindingResponse[] }> {
  const map = new Map<string, FindingResponse[]>()
  const multi: FindingResponse[] = []

  for (const f of findings) {
    if (f.occurrence_count > 1) {
      multi.push(f)
    } else {
      const bucket = map.get(f.file) ?? []
      bucket.push(f)
      map.set(f.file, bucket)
    }
  }

  const buckets: Array<{ file: string; findings: FindingResponse[] }> = []
  for (const [file, items] of map.entries()) {
    buckets.push({ file, findings: sortFindings(items) })
  }

  // Sort buckets by the worst severity present
  buckets.sort((a, b) => {
    const aWorst = Math.min(...a.findings.map((f) => severityOrder(f.severity)))
    const bWorst = Math.min(...b.findings.map((f) => severityOrder(f.severity)))
    return aWorst - bWorst
  })

  if (multi.length > 0) {
    buckets.push({ file: '__multi__', findings: sortFindings(multi) })
  }

  return buckets
}

/** Within a file bucket, group findings by subcategory.
 *  Each group sorts by worst severity, then by total estimated effort descending.
 *  Within each group, findings sort by line_start ascending.
 */
interface SubcategoryGroup {
  subcategory: string
  label: string
  worstSeverity: Severity
  totalEffort: number
  findings: FindingResponse[]
}

function groupBySubcategory(findings: FindingResponse[]): SubcategoryGroup[] {
  const map = new Map<string, FindingResponse[]>()

  for (const f of findings) {
    const bucket = map.get(f.subcategory) ?? []
    bucket.push(f)
    map.set(f.subcategory, bucket)
  }

  const groups: SubcategoryGroup[] = []
  for (const [subcategory, items] of map.entries()) {
    // Sort items by line_start ascending within the group
    const sorted = [...items].sort((a, b) => {
      const al = a.line_start ?? 0
      const bl = b.line_start ?? 0
      return al - bl
    })

    const worstOrder = Math.min(...items.map((f) => severityOrder(f.severity)))
    const worstSeverity = SEVERITIES[worstOrder] ?? 'low'
    const totalEffort = items.reduce((sum, f) => sum + f.estimated_effort_hours, 0)

    groups.push({
      subcategory,
      label: SUBCATEGORY_LABELS[subcategory] ?? subcategory.replace(/_/g, ' '),
      worstSeverity,
      totalEffort,
      findings: sorted,
    })
  }

  // Sort groups: worst severity first, then by total effort descending
  groups.sort((a, b) => {
    const sd = severityOrder(a.worstSeverity) - severityOrder(b.worstSeverity)
    if (sd !== 0) return sd
    return b.totalEffort - a.totalEffort
  })

  return groups
}

function buildGithubFileUrl(repoUrl: string, branch: string, file: string): string {
  return `${repoUrl.replace(/\.git$/, '')}/blob/${branch}/${file}`
}

function isGithub(repoUrl: string): boolean {
  return repoUrl.startsWith('https://github.com/')
}

// ---------------------------------------------------------------------------
// FileSectionHeader — shown in "by file" mode
// ---------------------------------------------------------------------------

interface FileSectionHeaderProps {
  file: string
  count: number
  repoUrl: string
  branch: string
}

function FileSectionHeader({ file, count, repoUrl, branch }: FileSectionHeaderProps) {
  const isMulti = file === '__multi__'
  const label = isMulti ? 'Multiple files' : file
  const linked = !isMulti && isGithub(repoUrl)

  return (
    <div className="flex items-center gap-2 py-2 border-b border-hairline mb-2">
      <div className="h-px flex-1 bg-fill-subtle" />
      <div className="flex items-center gap-1.5 shrink-0">
        {!isMulti && <FileCode className="w-3.5 h-3.5 text-faint" />}
        {linked ? (
          <a
            href={buildGithubFileUrl(repoUrl, branch, file)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[13px] tabular-nums text-body hover:text-accent-vivid flex items-center gap-1"
          >
            {label}
            <ExternalLink className="w-3 h-3 text-faint" />
          </a>
        ) : (
          <span className="text-[13px] tabular-nums text-body">{label}</span>
        )}
        <span className="text-[11px] text-faint ml-1">
          {count} finding{count !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="h-px flex-1 bg-fill-subtle" />
    </div>
  )
}

// ---------------------------------------------------------------------------
// SubcategoryGroupRow — collapsible finding-type group within a file bucket
// ---------------------------------------------------------------------------

interface SubcategoryGroupRowProps {
  group: SubcategoryGroup
  repoUrl: string
  branch: string
  scanId: string
  onVerdictChange: (findingId: string, verdict: FeedbackVerdict | null) => void
}

function SubcategoryGroupRow({ group, repoUrl, branch, scanId, onVerdictChange }: SubcategoryGroupRowProps) {
  const [open, setOpen] = useState(false)
  const count = group.findings.length

  return (
    <div className="rounded-lg shadow-card border border-hairline overflow-hidden bg-panel">
      {/* Group header — always visible */}
      <button
        // Hovers in the group's worst severity rather than in violet (DEC-133).
        data-hue={group.worstSeverity}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:shadow-hue-bar transition-shadow"
        onClick={() => setOpen((v) => !v)}
      >
        {open
          ? <ChevronDown className="w-3.5 h-3.5 text-faint shrink-0" />
          : <ChevronRight className="w-3.5 h-3.5 text-faint shrink-0" />
        }

        <span className="text-[13px] text-body font-medium flex-1 text-left">
          {group.label}
        </span>

        <span className="text-[11px] text-faint mr-1">({count})</span>

        <span className="w-[92px] flex-none">
          <SevPill severity={group.worstSeverity} />
        </span>
      </button>

      {/* Expanded findings */}
      {open && (
        <div className="border-t border-hairline divide-y divide-hairline">
          {group.findings.map((f) => (
            <FindingCard
              key={f.finding_id}
              finding={f}
              repoUrl={repoUrl}
              branch={branch}
              hideFile
              scanId={scanId}
              onVerdictChange={onVerdictChange}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function FindingsPage() {
  const { scanId } = useParams<{ scanId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const tab: PageTab = searchParams.get('tab') === 'checked' ? 'checked' : 'findings'

  function selectTab(next: PageTab) {
    // Functional update so every other parameter (?file=, ?category=) survives.
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      if (next === 'checked') params.set('tab', 'checked')
      else params.delete('tab')
      return params
    })
  }

  // Initialise from query params — ?file= (from GraphPage) and ?category= (from AgentCard)
  const initialFile = searchParams.get('file') ?? ''
  const [groupMode, setGroupMode] = useState<GroupMode>(initialFile ? 'file' : 'severity')
  const [activeSeverity, setActiveSeverity] = useState<Severity | null>(null)
  const [activeCategory, setActiveCategory] = useState<string | null>(
    searchParams.get('category'),
  )
  const [fileFilter, setFileFilter] = useState(initialFile)
  // Set by "See its N findings" (DEC-189): the category filter, named as the agent.
  const [agentFilter, setAgentFilter] = useState<string | null>(null)

  function filterByCategory(cat: string | null) {
    setActiveCategory(cat)
    setActiveSeverity(null)
    setAgentFilter(null)
  }

  function seeAgentFindings(agentId: string) {
    // One category per agent today; the first is the filter the list supports.
    const [category] = agentCategories(agentId)
    setActiveCategory(category ?? null)
    setActiveSeverity(null)
    setAgentFilter(agentId)
    selectTab('findings')
  }

  // Verdict overrides: optimistic local state on top of API data
  const [verdictOverrides, setVerdictOverrides] = useState<Map<string, FeedbackVerdict | null>>(
    () => new Map(),
  )

  function handleVerdictChange(findingId: string, verdict: FeedbackVerdict | null) {
    setVerdictOverrides((prev) => {
      const next = new Map(prev)
      next.set(findingId, verdict)
      return next
    })
  }

  function effectiveVerdict(f: FindingResponse): FeedbackVerdict | null {
    return verdictOverrides.has(f.finding_id)
      ? (verdictOverrides.get(f.finding_id) ?? null)
      : (f.feedback_verdict ?? null)
  }

  const { data: report, isLoading: reportLoading } = useQuery({
    queryKey: ['report', scanId],
    queryFn: () => scansApi.report(scanId!),
    enabled: !!scanId,
  })

  const { data: findingsData, isLoading: findingsLoading } = useQuery({
    queryKey: ['findings', scanId, activeCategory, activeSeverity],
    queryFn: () =>
      scansApi.findings(scanId!, {
        category: activeCategory ?? undefined,
        severity: activeSeverity ?? undefined,
        limit: 500,
      }),
    enabled: !!scanId,
  })

  const isLoading = reportLoading || findingsLoading

  // All findings from API
  const allApiFindings: FindingResponse[] = findingsData?.findings ?? []

  // Suppressed = wont_fix (by effective verdict). Not affected by text filter.
  const suppressedFindings: FindingResponse[] = allApiFindings.filter(
    (f) => effectiveVerdict(f) === 'wont_fix',
  )

  // Active = not suppressed, then apply file filter and sort
  const findings: FindingResponse[] = sortFindings(
    allApiFindings
      .filter((f) => effectiveVerdict(f) !== 'wont_fix')
      .filter((f) => !fileFilter || f.file.toLowerCase().includes(fileFilter.toLowerCase())),
  )

  const repoUrl = report?.repo_url ?? ''
  const branch = report?.branch ?? 'main'

  // ---------------------------------------------------------------------------
  // Loading / error states
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <AppShell scanId={scanId} crumb="Findings" active="report">
        <div className="h-full flex items-center justify-center">
          <Spinner />
        </div>
      </AppShell>
    )
  }

  if (!report) {
    return (
      <AppShell scanId={scanId} crumb="Findings" active="report">
        <div className="h-full flex items-center justify-center text-muted">
          Report not found.
        </div>
      </AppShell>
    )
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const fileBuckets = groupMode === 'file' ? groupByFile(findings) : []

  return (
    // The Dependency graph is a tab of the scan header now (DEC-194), so the Graph
    // button that was its only way in is gone.
    <AppShell scanId={scanId} crumb="Findings" active="report">
      <main className="max-w-6xl mx-auto w-full px-6 py-8">
        <p className="text-[11px] text-faint mb-3">
          Evaluated <span className="font-medium text-muted">{dateTimeLabel(report.started_at)}</span>
          {ranHeader(report) && <span data-testid="report-run-time"> · {ranHeader(report)}</span>}
        </p>
        <ScanModeBanner
          llmMode={report.llm_mode}
          reason={report.llm_degraded_reason}
          fromAgent={report.llm_degraded_from_agent}
        />
        <ScanSourceManifestNote scanId={scanId} />
        <div className="flex gap-8">
        {/* Left sidebar — summary panel */}
        <aside className="w-56 shrink-0">
          <SummaryPanel
            report={report}
            onCategoryClick={(cat) => {
              filterByCategory(cat)
              selectTab('findings')
            }}
            activeCategory={activeCategory}
            onSeverityClick={(sev) => {
              setActiveSeverity(sev)
              setActiveCategory(null)
              setAgentFilter(null)
              selectTab('findings')
            }}
            activeSeverity={activeSeverity}
          />
        </aside>

        {/* Main column: two tabs (DEC-189) */}
        <div className="flex-1 min-w-0">
          <div role="tablist" aria-label="Report sections" className="flex gap-1 border-b border-hairline mb-5">
            <TabButton
              selected={tab === 'findings'}
              onClick={() => selectTab('findings')}
              label="Findings"
              count={String(report.summary?.total_findings ?? 0)}
            />
            <TabButton
              selected={tab === 'checked'}
              onClick={() => selectTab('checked')}
              label="What TDIA checked"
              count={
                report.coverage_panel && !report.coverage_panel.coverage_not_recorded
                  ? `${report.coverage_panel.applicable} applicable`
                  : null
              }
            />
          </div>

          {tab === 'checked' ? (
            <div role="tabpanel" aria-label="What TDIA checked">
              <CheckedTab
                panel={report.coverage_panel}
                notExamined={report.not_examined}
                findingsByCategory={report.summary?.findings_by_category ?? {}}
                onSeeFindings={seeAgentFindings}
              />
            </div>
          ) : (
          <div role="tabpanel" aria-label="Findings">
          {/* Filter bar */}
          <div className="flex flex-wrap items-center gap-2 mb-5">
            {/* Group-mode toggle */}
            <div className="flex gap-0.5 p-0.5 bg-fill-subtle rounded-lg">
              {(['severity', 'file'] as GroupMode[]).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setGroupMode(mode)}
                  className={`px-3 py-1 rounded-md text-[12px] font-medium transition-colors capitalize ${
                    groupMode === mode
                      ? 'bg-surface-active text-ink'
                      : 'text-muted hover:text-body'
                  }`}
                >
                  By {mode}
                </button>
              ))}
            </div>

            {/* Severity filter */}
            <div className="flex gap-1">
              <button
                onClick={() => setActiveSeverity(null)}
                className={`text-[12px] px-2.5 py-1 rounded-md border transition-colors ${
                  activeSeverity === null
                    ? 'bg-surface-active text-ink border-hairline-strong'
                    : 'border-hairline text-body hover:border-hairline-strong'
                }`}
              >
                All
              </button>
              {SEVERITIES.map((s) => (
                <button
                  key={s}
                  onClick={() => setActiveSeverity(activeSeverity === s ? null : s)}
                  // `data-hue` makes the hover border this severity's own colour,
                  // replacing a hardcoded per-severity map (DEC-133). The old
                  // `hover:opacity-75` faded the chip toward the page, so
                  // hovering made the target harder to see (DEC-132).
                  data-hue={s}
                  className={`inline-flex items-center gap-[6px] text-[12px] px-2.5 py-1 rounded-md border transition-colors ${
                    activeSeverity === s
                      ? 'bg-surface-active text-ink border-hairline-strong'
                      : 'bg-panel text-ink border-hairline hover:border-hue/50'
                  }`}
                >
                  <PillDot tone={s} />
                  {SEVERITY_LABEL[s]}
                </button>
              ))}
            </div>

            {/* File filter */}
            <input
              type="text"
              value={fileFilter}
              onChange={(e) => setFileFilter(e.target.value)}
              placeholder="Filter by file…"
              className="ml-auto border border-control rounded-md px-2.5 py-1 text-[13px] text-body placeholder-faint focus:outline-none focus-visible:shadow-focus w-48"
            />
          </div>

          {/* Active filter chips */}
          {(activeCategory || activeSeverity) && (
            <div className="flex flex-wrap gap-2 mb-4">
              {activeCategory && (
                <button
                  onClick={() => filterByCategory(null)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent/[0.14] text-accent-text text-[12px]"
                  aria-label={`Clear filter: ${agentFilter ? `Only ${agentName(agentFilter)}` : activeCategory}`}
                >
                  {agentFilter ? `Only ${agentName(agentFilter)}` : activeCategory} ×
                </button>
              )}
              {activeSeverity && (
                <button
                  onClick={() => setActiveSeverity(null)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent/[0.14] text-accent-text text-[12px]"
                >
                  {activeSeverity} ×
                </button>
              )}
            </div>
          )}

          {/* Count + suppressed note */}
          <div className="flex items-baseline gap-3 mb-3">
            <p className="text-[12px] text-muted">
              {findings.length} finding{findings.length !== 1 ? 's' : ''}
              {findingsData && findings.length < findingsData.total
                ? ` (filtered from ${findingsData.total})`
                : ''}
            </p>
            {suppressedFindings.length > 0 && (
              <p className="text-[11px] text-warn">
                {suppressedFindings.length} suppressed (Won't Fix), not included in totals
              </p>
            )}
          </div>

          {/* Finding cards */}
          {findings.length === 0 ? (
            <div className="text-[14px] text-faint py-12 text-center">
              No findings match the current filters.
            </div>
          ) : groupMode === 'severity' ? (
            /* ── By severity: sectioned by severity level ── */
            <div className="space-y-6">
              {SEVERITIES.map((sev) => {
                const group = findings.filter((f) => f.severity === sev)
                if (group.length === 0) return null
                return (
                  <div key={sev}>
                    <div className="flex items-center gap-3 mb-2">
                      <SevPill severity={sev}>
                        {SEVERITY_LABEL[sev]} · {group.length}
                      </SevPill>
                      <div className="h-px flex-1 bg-fill-subtle" />
                    </div>
                    <div className="space-y-2">
                      {group.map((f) => (
                        <FindingCard
                          key={f.finding_id}
                          finding={f}
                          repoUrl={repoUrl}
                          branch={branch}
                          scanId={scanId}
                          onVerdictChange={handleVerdictChange}
                        />
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            /* ── By file: file buckets → subcategory accordion groups ── */
            <div className="space-y-6">
              {fileBuckets.map(({ file, findings: bucketFindings }) => {
                const subcatGroups = groupBySubcategory(bucketFindings)
                return (
                  <div key={file}>
                    <FileSectionHeader
                      file={file}
                      count={bucketFindings.length}
                      repoUrl={repoUrl}
                      branch={branch}
                    />
                    <div className="space-y-1.5">
                      {subcatGroups.map((group) => (
                        <SubcategoryGroupRow
                          key={group.subcategory}
                          group={group}
                          repoUrl={repoUrl}
                          branch={branch}
                          scanId={scanId!}
                          onVerdictChange={handleVerdictChange}
                        />
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* ── Suppressed section (Won't Fix) [DEC-045] ── */}
          <SuppressedSection
            findings={suppressedFindings}
            repoUrl={repoUrl}
            branch={branch}
            scanId={scanId!}
            onVerdictChange={handleVerdictChange}
          />
          </div>
          )}
        </div>
      </div>
      </main>
    </AppShell>
  )
}

// ---------------------------------------------------------------------------
// TabButton — one of the two report tabs (DEC-189)
// ---------------------------------------------------------------------------

interface TabButtonProps {
  selected: boolean
  onClick: () => void
  label: string
  count: string | null
}

function TabButton({ selected, onClick, label, count }: TabButtonProps) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      onClick={onClick}
      className={`-mb-px inline-flex items-center gap-2 px-[18px] py-3 text-[15px] font-semibold border-b-2 transition-colors focus:outline-none focus-visible:shadow-focus ${
        selected ? 'text-ink border-accent' : 'text-faint border-transparent hover:text-body'
      }`}
    >
      {label}
      {count !== null && (
        <span
          className={`text-[12px] font-medium tabular-nums px-[7px] py-px rounded-full ${
            selected ? 'bg-surface-active text-ink' : 'bg-fill-subtle text-muted'
          }`}
        >
          {count}
        </span>
      )}
    </button>
  )
}

// ---------------------------------------------------------------------------
// SuppressedSection — WhatsApp Archive model for Won't Fix findings [DEC-045]
// ---------------------------------------------------------------------------

interface SuppressedSectionProps {
  findings: FindingResponse[]
  repoUrl: string
  branch: string
  scanId: string
  onVerdictChange: (findingId: string, verdict: FeedbackVerdict | null) => void
}

function SuppressedSection({ findings, scanId, onVerdictChange }: SuppressedSectionProps) {
  const [open, setOpen] = useState(false)
  const [restoreErrors, setRestoreErrors] = useState<Record<string, string>>({})

  const restoreMutation = useMutation({
    mutationFn: (findingId: string) => scansApi.deleteFeedback(scanId, findingId),
    onSuccess: (_data, findingId) => {
      setRestoreErrors((prev) => { const n = { ...prev }; delete n[findingId]; return n })
      onVerdictChange(findingId, null)
    },
    onError: (_err, findingId) =>
      setRestoreErrors((prev) => ({ ...prev, [findingId]: 'Could not restore. Try again.' })),
  })

  if (findings.length === 0) return null

  return (
    <div className="mt-8 border border-warn/30 rounded-lg overflow-hidden">
      {/* Header — always visible */}
      <button
        className="w-full flex items-center gap-2 px-4 py-3 bg-chip-warn hover:bg-chip-warn transition-colors text-left"
        onClick={() => setOpen((v) => !v)}
      >
        {open
          ? <ChevronDown className="w-4 h-4 text-warn shrink-0" />
          : <ChevronRight className="w-4 h-4 text-warn shrink-0" />
        }
        <span className="text-[13px] font-medium text-warn flex-1">
          Suppressed ({findings.length})
        </span>
        <span className="text-[11px] text-warn">
          Won't Fix: acknowledged trade-offs
        </span>
      </button>

      {/* Expanded list */}
      {open && (
        <div className="divide-y divide-warn/20 bg-panel">
          {findings.map((f) => (
            <div key={f.finding_id} className="flex items-start gap-3 px-4 py-3">
              {/* Compact finding info */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-0.5">
                  <span className="w-[92px] flex-none">
                    <SevPill severity={f.severity} />
                  </span>
                  <span className="text-[12px] text-body truncate">
                    {f.human_summary.slice(0, 80)}{f.human_summary.length > 80 ? '…' : ''}
                  </span>
                </div>
                <p className="text-[12px] text-faint tabular-nums">
                  {f.file}{f.line_start !== null ? `:${f.line_start}` : ''}
                </p>
              </div>

              {/* Restore button */}
              <div className="shrink-0 flex flex-col items-end gap-0.5">
                <button
                  className="text-[12px] font-medium text-warn hover:text-ink border border-warn/40 hover:border-warn rounded px-2 py-1 transition-colors disabled:opacity-50"
                  onClick={() => restoreMutation.mutate(f.finding_id)}
                  disabled={restoreMutation.isPending}
                  title="Remove Won't Fix: restore to main findings list"
                >
                  {restoreMutation.isPending && restoreMutation.variables === f.finding_id
                    ? '…'
                    : '↑ Restore'}
                </button>
                {restoreErrors[f.finding_id] && (
                  <span className="text-[10px] text-critical">{restoreErrors[f.finding_id]}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
